"use client";

import { useI18n } from "@/i18n";

const styles = {
    page: {
        minHeight: "100vh",
        background: "#F5F7F5",
        paddingTop: 96,
        paddingBottom: 80,
    },
    container: {
        maxWidth: 820,
        margin: "0 auto",
        padding: "0 24px",
    },
    card: {
        background: "#ffffff",
        borderRadius: 16,
        border: "1px solid #ECEEEC",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
        padding: "48px 56px",
    },
    title: {
        fontSize: 32,
        fontWeight: 800,
        color: "#111827",
        margin: 0,
        letterSpacing: "-0.01em",
    },
    subtitle: {
        fontSize: 14,
        color: "#6B7280",
        marginTop: 10,
        marginBottom: 28,
        paddingBottom: 24,
        borderBottom: "1px solid #F1F3F1",
    },
    hero: {
        display: "flex",
        gap: 16,
        alignItems: "flex-start",
        padding: "18px 20px",
        background: "#F0FDF4",
        border: "1px solid #BBF7D0",
        borderRadius: 14,
        marginTop: 4,
        marginBottom: 8,
    },
    heroIcon: { fontSize: 28, lineHeight: 1 },
    heroText: { fontSize: 14.5, lineHeight: 1.7, color: "#166534", fontWeight: 500 },
    section: { marginTop: 32 },
    sectionTitle: {
        fontSize: 18,
        fontWeight: 700,
        color: "#111827",
        margin: "0 0 12px",
        display: "flex",
        alignItems: "center",
        gap: 10,
    },
    sectionNum: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 26,
        height: 26,
        borderRadius: 8,
        background: "#2F9E44",
        color: "#fff",
        fontSize: 13,
        fontWeight: 700,
        flexShrink: 0,
    },
    sectionBody: { fontSize: 15, lineHeight: 1.8, color: "#374151" },
    tipGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 14,
        marginTop: 14,
    },
    tipCard: {
        border: "1px solid #ECEEEC",
        borderRadius: 12,
        padding: "16px 18px",
        background: "#ffffff",
    },
    tipIcon: { fontSize: 20, marginBottom: 6 },
    tipTitle: { fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 4 },
    tipDesc: { fontSize: 13, lineHeight: 1.55, color: "#6B7280" },
    warning: {
        background: "#FEF3C7",
        border: "1px solid #FDE68A",
        borderRadius: 12,
        padding: "16px 18px",
        marginTop: 14,
        fontSize: 14,
        lineHeight: 1.7,
        color: "#92400E",
    },
    faqItem: { borderBottom: "1px solid #F1F3F1", padding: "14px 0" },
    faqItemLast: { borderBottom: "none" },
    faqQ: { fontSize: 14.5, fontWeight: 700, color: "#111827", marginBottom: 6 },
    faqA: { fontSize: 14, lineHeight: 1.75, color: "#4B5563" },
    contactBox: {
        marginTop: 32,
        padding: "20px 24px",
        background: "#F4F6F4",
        borderRadius: 12,
        fontSize: 14,
        color: "#374151",
        lineHeight: 1.8,
    },
};

function Section({ num, title, children }) {
    return (
        <section style={styles.section}>
            <h2 style={styles.sectionTitle}>
                <span style={styles.sectionNum}>{num}</span>
                {title}
            </h2>
            <div style={styles.sectionBody}>{children}</div>
        </section>
    );
}

export default function SafetyPage() {
    const { t } = useI18n();

    const buyerTips = t("safety.buyerTips");
    const sellerTips = t("safety.sellerTips");
    const scamList = t("safety.scam");
    const faqs = t("safety.faq");

    return (
        <div style={styles.page}>
            <div style={styles.container}>
                <article style={styles.card}>
                    <h1 style={styles.title}>{t("safety.title")}</h1>
                    <div style={styles.subtitle}>{t("safety.subtitle")}</div>

                    <div style={styles.hero}>
                        <div style={styles.heroIcon}>🛡️</div>
                        <div style={styles.heroText}>{t("safety.intro")}</div>
                    </div>

                    {/* Buyer tips */}
                    <Section num="1" title={t("safety.buyer")}>
                        <div style={styles.tipGrid}>
                            {Object.entries(buyerTips || {}).map(([key, val]) => {
                                const iconMap = {
                                    photos: "📸", verifyInfo: "🔍", checkDocs: "📄",
                                    noPrepay: "💰", publicPlace: "📍", bringExpert: "🚗",
                                };
                                return (
                                    <div key={key} style={styles.tipCard}>
                                        <div style={styles.tipIcon}>{iconMap[key] || "✅"}</div>
                                        <div style={styles.tipTitle}>{typeof val === "object" ? val.title || val.label || "" : val}</div>
                                        {typeof val === "object" && (
                                            <div style={styles.tipDesc}>{val.desc || val.description || ""}</div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </Section>

                    {/* Seller tips */}
                    <Section num="2" title={t("safety.seller")}>
                        <div style={styles.tipGrid}>
                            {Object.entries(sellerTips || {}).map(([key, val]) => {
                                const iconMap = {
                                    honest: "✅", written: "📝", securePayment: "💳",
                                    privacy: "🔐", transfer: "🏦",
                                };
                                return (
                                    <div key={key} style={styles.tipCard}>
                                        <div style={styles.tipIcon}>{iconMap[key] || "✅"}</div>
                                        <div style={styles.tipTitle}>{typeof val === "object" ? val.title || val.label || "" : val}</div>
                                        {typeof val === "object" && (
                                            <div style={styles.tipDesc}>{val.desc || val.description || ""}</div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </Section>

                    {/* Scam signals */}
                    <Section num="3" title={t("safety.scamSignals")}>
                        <div style={styles.warning}>
                            <strong>🚨</strong>
                            <ul style={{ margin: "10px 0 0", paddingLeft: 18 }}>
                                {(Array.isArray(scamList) ? scamList : []).map((s, i) => (
                                    <li key={i} style={{ marginBottom: 4 }}>{typeof s === "string" ? s : s}</li>
                                ))}
                            </ul>
                        </div>
                    </Section>

                    {/* FAQ */}
                    <Section num="4" title={t("safety.faqTitle")}>
                        <div style={{ marginTop: -14 }}>
                            {(Array.isArray(faqs) ? faqs : []).map((f, i, arr) => (
                                <div
                                    key={i}
                                    style={{
                                        ...styles.faqItem,
                                        ...(i === arr.length - 1 ? styles.faqItemLast : {}),
                                    }}
                                >
                                    <div style={styles.faqQ}>Q: {f?.q || ""}</div>
                                    <div style={styles.faqA}>{f?.a || ""}</div>
                                </div>
                            ))}
                        </div>
                    </Section>

                    <div style={styles.contactBox}>
                        <div style={{ fontWeight: 700, marginBottom: 6 }}>{t("safety.contactUs")}</div>
                        <div>{t("safety.reportDesc")}</div>
                        <div style={{ marginTop: 8 }}>
                            {t("safety.reportTitle")}：
                            <a href={`mailto:${t("safety.contactEmail")}`} style={{ color: "#2F9E44" }}>
                                {t("safety.contactEmail")}
                            </a>
                        </div>
                    </div>
                </article>
            </div>
        </div>
    );
}
